import React from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { FormApi } from 'final-form';
import { DateTime } from 'luxon';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { PermissiblePurposeStatus, VerifierCredentialStatus, VerifierInfoFragment } from 'src/api';
import { Button, FullscreenLoader } from 'src/components/common';
import {
  RadioGroupField,
  RadioGroupOption,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { useManualVerifyPermissiblePurposeReferenceOptions } from 'src/hooks/permissiblePurposeReference/useManualVerifyPermissiblePurposeReferenceOptions';
import {
  usePermissiblePurposeCreate,
  usePermissiblePurposeUpdate,
} from 'src/hooks/permissiblePurposes';
import { useVerifierById } from 'src/hooks/verifier';

import { VerifyManuallyModalFields, VerifyManuallyModalValues } from './types';
import { verifyAutomaticModalFormValidationSchema } from './utils';

export interface VerifierVerifyManuallyByCVStaffModalFormProps {
  onCloseModal: () => void;
  verifier: VerifierInfoFragment | undefined;
  switchClosability: (closability: boolean) => void;
}

export const VerifierVerifyManuallyByCVStaffModalForm: React.FC<
  VerifierVerifyManuallyByCVStaffModalFormProps
> = ({ switchClosability, onCloseModal, verifier }) => {
  const { updateVerifier } = useVerifierById(verifier?.id);
  const { createPermissiblePurpose } = usePermissiblePurposeCreate();
  const { updatePermissiblePurpose } = usePermissiblePurposeUpdate();

  const {
    permissiblePurposeReferenceOptions,
    havePermissiblePurposePending,
    isPermissiblePurposeReferencesLoading,
    getRelatedPermissiblePurpose,
  } = useManualVerifyPermissiblePurposeReferenceOptions(verifier);

  const handleSubmit = React.useCallback(
    async (formValues: VerifyManuallyModalValues) => {
      if (!verifier?.id) return;

      try {
        const permissiblePurposeInDB = getRelatedPermissiblePurpose(
          formValues.permissiblePurposeReference,
        );
        const isPermissiblePurposeInTheForm = Boolean(formValues.permissiblePurposeReference);

        const denialReason =
          formValues.status === VerifierCredentialStatus.Denied ? formValues.denialReason : null;
        const permissiblePurposeStatus =
          formValues.status === VerifierCredentialStatus.Credentialed
            ? PermissiblePurposeStatus.Approved
            : PermissiblePurposeStatus.Declined;

        await updateVerifier({
          credentialStatus: formValues.status,
          credentialedAt:
            formValues.status === VerifierCredentialStatus.Credentialed
              ? DateTime.now().toISO()
              : null,
          denialReason,
        });

        if (isPermissiblePurposeInTheForm) {
          if (permissiblePurposeInDB?.id) {
            await updatePermissiblePurpose({
              variables: {
                id: permissiblePurposeInDB.id,
                data: {
                  status: permissiblePurposeStatus,
                  denialReason,
                },
              },
            });
          } else {
            await createPermissiblePurpose({
              variables: {
                data: {
                  verifier: {
                    connect: { id: verifier.id },
                  },
                  denialReason,
                  status: permissiblePurposeStatus,
                  permissiblePurposeReference: {
                    connect: {
                      id: formValues.permissiblePurposeReference!,
                    },
                  },
                },
              },
            });
          }
        }

        onCloseModal();
      } catch (error) {
        console.error(error);
      } finally {
        switchClosability(true);
      }
    },
    [
      createPermissiblePurpose,
      getRelatedPermissiblePurpose,
      onCloseModal,
      switchClosability,
      updatePermissiblePurpose,
      updateVerifier,
      verifier?.id,
    ],
  );

  const statusOptions: RadioGroupOption[] = React.useMemo(
    () => [
      {
        label: <React.Fragment>Approved</React.Fragment>,
        value: VerifierCredentialStatus.Credentialed,
        disabled: verifier?.credentialStatus === VerifierCredentialStatus.Credentialed,
      },
      {
        label: <React.Fragment>Denied</React.Fragment>,
        value: VerifierCredentialStatus.Denied,
        disabled: verifier?.credentialStatus === VerifierCredentialStatus.Denied,
      },
    ],
    [verifier?.credentialStatus],
  );

  const initialValues = React.useMemo(() => {
    const getStatus = () => {
      if (verifier?.credentialStatus === VerifierCredentialStatus.Credentialed) {
        return VerifierCredentialStatus.Denied;
      }
      return VerifierCredentialStatus.Credentialed;
    };
    return {
      denialReason: verifier?.denialReason ?? '',
      status: getStatus(),
      permissiblePurposeReference: havePermissiblePurposePending
        ? permissiblePurposeReferenceOptions[0]?.value
        : null,
    };
  }, [
    havePermissiblePurposePending,
    permissiblePurposeReferenceOptions,
    verifier?.credentialStatus,
    verifier?.denialReason,
  ]);

  const handleInputChange =
    (form: FormApi<VerifyManuallyModalValues>) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value === VerifierCredentialStatus.Denied && havePermissiblePurposePending) {
        form.change(
          VerifyManuallyModalFields.permissiblePurposeReference,
          permissiblePurposeReferenceOptions[0]?.value,
        );
      }
      form.change(VerifyManuallyModalFields.status, event.target.value as VerifierCredentialStatus);
    };

  return (
    <Form<VerifyManuallyModalValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateWithYupSchema(verifyAutomaticModalFormValidationSchema)}
    >
      {({ submitting, values, valid, form }) => (
        <React.Fragment>
          {isPermissiblePurposeReferencesLoading ? (
            <FullscreenLoader />
          ) : (
            <Grid container gap={1} size={12}>
              <Typography variant="body1" color="text.secondary">
                Please submit the Verification results for this verifier below. Upon submitting, we
                will with email them with either Verification confirmation or denial reason.
              </Typography>
              <Field
                component={RadioGroupField}
                name={VerifyManuallyModalFields.status}
                options={statusOptions}
                fullWidth
                onChange={handleInputChange(form)}
                disabled={submitting || isPermissiblePurposeReferencesLoading}
                required
              />
              {values.status === VerifierCredentialStatus.Denied && (
                <Field
                  name={VerifyManuallyModalFields.denialReason}
                  label="Denial Reason"
                  fullWidth
                  required
                  disabled={submitting || isPermissiblePurposeReferencesLoading}
                  component={TextField}
                  sx={{ m: '8px 0 24px 0' }}
                />
              )}
              {havePermissiblePurposePending && (
                <Field
                  name={VerifyManuallyModalFields.permissiblePurposeReference}
                  component={SelectField}
                  options={permissiblePurposeReferenceOptions}
                  scrollableContent
                  fullWidth
                  required
                  disabled={submitting || values.status === VerifierCredentialStatus.Denied}
                />
              )}
            </Grid>
          )}
          <Box display="flex" gap={2} mt={4} justifyContent="center">
            <Button
              variant="outlined"
              color="infoButton"
              onClick={onCloseModal}
              disabled={submitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || isPermissiblePurposeReferencesLoading || !valid}
              loading={submitting || isPermissiblePurposeReferencesLoading}
            >
              Submit
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};
